import { useEffect, useState } from "react";
import { Field, FormSpy } from "react-final-form";
import { TitlePage, PhoneField, CustomButton, Icon, RadioField, DatePickerField } from "~/components";
import { composeValidators, disabledValidate, notEmptyValue, required, testRegExp, validateDependentFields, validateMinAge, validateRelatedFields } from "~/utils/validate";
import { Divider } from "antd";
import { CustomInnField, FormField, FormFieldAutocomplete, FormFieldAutocompleteAddress } from "~/pages/questionnaires/shared";
import { ClientId, NamesOfFieldEnum, OrgTypeEnum, TypeSuggestionData } from "~/typings/types";
import { configTitleFieldsOfIndividualEntrepreneur } from "../configTitleFields";
import cn from "classnames";
import { V2Wizard } from "../../../../shared/wizard";
import { getCrmNameByOrgType } from "../../../../shared/utils/getCrmNameByOrgType";
import { FullWidthRowFormContainer } from "~/pages/questionnaires/shared/form-field";
import { PERSONAL_DATA_ADDRESS_SUGGEST, PERSONAL_DATA_FULLNAME_SUGGEST } from "../../../../api/apiUrls";
import { RenderError } from "~/components/error-field";
import { UseFormValueTracker, useScrollAndFocusField } from "~/hooks";
import styles from "./CommonSteps.module.scss";
import '../../styles.scss';
import { getTitleFieldCrmPhoneByOrgType } from "~/utils";
import { useAnalyticsContext } from "~/services/analytics";

interface AccountDetailsProps {
  clientId: ClientId;
  orgType: OrgTypeEnum;
  isShowAdditionalFieldsInitially?: boolean;
}

export const AccountDetails = ({
  clientId,
  orgType,
  isShowAdditionalFieldsInitially = false,
}: AccountDetailsProps) => {
  const [showAdditionalFields, setShowAdditionalFields] = useState(isShowAdditionalFieldsInitially);
  const [isCorrectFullnameByInn, setIsCorrectFullnameByInn] = useState(false);

  const analytics = useAnalyticsContext();
  analytics?.trackStep('anketa-customer-ie-init_step2');
  analytics?.trackStepWebvisor('webvisor_anketa_customer_step1');

  useScrollAndFocusField(NamesOfFieldEnum.Inn);

  const toggleAdditionalFields = () => setShowAdditionalFields(!showAdditionalFields);

  return (
    <V2Wizard.Page>
      <TitlePage
        name="Регистрация профиля в системе"
        number="2"
      />
      <Divider style={{ margin: 0 }} />
      <div className={styles.container}>
        <UseFormValueTracker
          fields={[
            NamesOfFieldEnum.CrmName,
            NamesOfFieldEnum.CrmPhone,
            NamesOfFieldEnum.CrmEmail,
            NamesOfFieldEnum.Inn,
            NamesOfFieldEnum.CrmBirthday,
            NamesOfFieldEnum.LegalAddress,
            NamesOfFieldEnum.Ogrnip,
          ]}
          analytics={analytics}
          stepName="anketa-customer-ie-inprogress_step2"
        />

        <FullWidthRowFormContainer>
          <CustomInnField
            innLength={12}
            orgType={orgType}
            clientId={clientId}
            configNameFields={configTitleFieldsOfIndividualEntrepreneur}
            setIsCorrectFullnameByInn={setIsCorrectFullnameByInn}
          />
        </FullWidthRowFormContainer>

        <FullWidthRowFormContainer>
          <FormSpy>
            {propsSpy => {

              const errorFullname = propsSpy?.submitErrors?.[NamesOfFieldEnum.CrmNameFirst]
                || propsSpy?.submitErrors?.[NamesOfFieldEnum.CrmNameLast]
                || propsSpy?.submitErrors?.[NamesOfFieldEnum.CrmNamePatron];

              const renderErrorFullname: RenderError = {
                has:
                  Boolean(errorFullname) && ((propsSpy.values?.[NamesOfFieldEnum.CrmNameFirst] && !propsSpy.values?.[NamesOfFieldEnum.CrmNameLast]) ||
                    (!propsSpy.values?.[NamesOfFieldEnum.CrmNameFirst] && propsSpy.values?.[NamesOfFieldEnum.CrmNameLast]))
                    ? 1
                    : 0,
                message: errorFullname
              };

              return (
                <FormFieldAutocomplete
                  titleField={getCrmNameByOrgType(OrgTypeEnum.IndividualEntrepreneur)}
                  name={NamesOfFieldEnum.CrmName}
                  keyValueAutocompleteSelected={("name" as keyof TypeSuggestionData)}
                  required
                  validate={composeValidators(required(), notEmptyValue(),
                    validateRelatedFields(
                      [NamesOfFieldEnum.CrmNameLast, NamesOfFieldEnum.CrmNameFirst],
                      "Укажите фамилию, имя и отчество через пробел и выберите из списка"
                    ))}
                  propsSpy={propsSpy}
                  clientId={clientId}
                  urlPersonalDataAddressSuggest={PERSONAL_DATA_FULLNAME_SUGGEST}
                  handleInputChangeFromProps={() => {
                    propsSpy.form.change(NamesOfFieldEnum.CrmNameFirst, '')
                    propsSpy.form.change(NamesOfFieldEnum.CrmNameLast, '')
                    propsSpy.form.change(NamesOfFieldEnum.CrmNamePatron, '')
                  }}
                  renderError={renderErrorFullname}
                  handleSuggestionClickFromProps={(suggestion) => {
                    if ('name' in suggestion) {
                      suggestion?.first_name && propsSpy.form.change(NamesOfFieldEnum.CrmNameFirst, suggestion.first_name);
                      propsSpy.form.change(NamesOfFieldEnum.CrmNameLast, suggestion?.last_name || '-');
                      suggestion?.patronymic_name && propsSpy.form.change(NamesOfFieldEnum.CrmNamePatron, suggestion.patronymic_name);
                      suggestion?.name && propsSpy.form.change(NamesOfFieldEnum.Name, suggestion.name);

                      setIsCorrectFullnameByInn(false);
                    }
                  }}
                  isRenderSuccessDescription={orgType === OrgTypeEnum.IndividualEntrepreneur && isCorrectFullnameByInn}
                  autoComplete="off"
                />
              );
            }}
          </FormSpy>
        </FullWidthRowFormContainer>

        <FormField
          titleField={configTitleFieldsOfIndividualEntrepreneur[NamesOfFieldEnum.CrmPhone]}
          name={NamesOfFieldEnum.CrmPhone}
          required
        >
          <Field
            name={NamesOfFieldEnum.CrmPhone}
            subscription={{ submitFailed: true, error: true, submitError: true, value: true }}
            component={props => <PhoneField
              required
              titleField={getTitleFieldCrmPhoneByOrgType(orgType, configTitleFieldsOfIndividualEntrepreneur[NamesOfFieldEnum.CrmPhone])}
              tooltip="Укажите номер телефона принадлежащий собственнику, который производит подключение. Если вы являетесь тех.специалистом или другим лицом заполняющим анкету, то укажите ваши данные в разделе дополнительные контакты"
              {...props} />}
            validate={disabledValidate(composeValidators(required()))}
          />
        </FormField>

        <FormField
          titleField={configTitleFieldsOfIndividualEntrepreneur[NamesOfFieldEnum.CrmEmail]}
          name={NamesOfFieldEnum.CrmEmail}
          required
          validate={composeValidators(
            required(),
            testRegExp(
              /^\s*\S+@\S+\.\S+\s*$/,
              "Пожалуйста, укажите корректный адрес электронной почты"
            )
          )}
        />

        <FormField
          titleField={configTitleFieldsOfIndividualEntrepreneur[NamesOfFieldEnum.CrmBirthday]}
          name={NamesOfFieldEnum.CrmBirthday}
          required
          validate={composeValidators(
            required(),
            validateMinAge()
          )}
          mask="99.99.9999"
        />

        <FormField
          titleField={configTitleFieldsOfIndividualEntrepreneur[NamesOfFieldEnum.Ogrnip]}
          name={NamesOfFieldEnum.Ogrnip}
          maxLength={15}
          mask="399999999999999"
          required
          validate={composeValidators(
            required(),
            testRegExp(/^3(\d){14}$/g, 'ОГРНИП должен начинаться на "3" и состоять из 15 цифр')
          )}
        />

        <FullWidthRowFormContainer>
          <FormSpy>
            {propsSpy => {
              return (
                <FormFieldAutocompleteAddress
                  titleField={configTitleFieldsOfIndividualEntrepreneur[NamesOfFieldEnum.LegalAddress]}
                  name={NamesOfFieldEnum.LegalAddress}
                  nameFiasId={NamesOfFieldEnum.LegalAddressFiasId}
                  required
                  validate={composeValidators(required(), notEmptyValue())}
                  propsSpy={propsSpy}
                  clientId={clientId}
                  urlPersonalDataAddressSuggest={PERSONAL_DATA_ADDRESS_SUGGEST}
                />
              );
            }}
          </FormSpy>
        </FullWidthRowFormContainer>

        <FullWidthRowFormContainer>
          <FormSpy>
            {propsSpy => {
              return (
                <>
                  <FormField
                    titleField="Почтовый адрес совпадает с адресом регистрации"
                    name={NamesOfFieldEnum.PostalAddressRadio}
                    showError={false}
                    required
                    shouldRenderTitleAboveField
                  >
                    <div className={styles.containerRadio} style={{ marginBottom: 16 }}>
                      <Field
                        name={NamesOfFieldEnum.PostalAddressRadio}
                        type="radio"
                        value="yes"
                        component={RadioField}
                      >
                        Да
                      </Field>
                      <Field
                        name={NamesOfFieldEnum.PostalAddressRadio}
                        type="radio"
                        value="no"
                        component={RadioField}
                      >
                        Нет
                      </Field>
                    </div>
                  </FormField>
                  {(propsSpy.values?.[NamesOfFieldEnum.PostalAddressRadio] === "no") && (
                    <FormFieldAutocompleteAddress
                      titleField={configTitleFieldsOfIndividualEntrepreneur[NamesOfFieldEnum.PostalAddress]}
                      name={NamesOfFieldEnum.PostalAddress}
                      nameFiasId={NamesOfFieldEnum.PostalAddressFiasId}
                      triggerAutofillTextButton="Совпадает с адресом регистрации"
                      tooltip="При необходимости направим оригиналы документов на этот адрес"
                      propsSpy={propsSpy}
                      clientId={clientId}
                      required
                      validate={composeValidators(required(), notEmptyValue())}
                      urlPersonalDataAddressSuggest={PERSONAL_DATA_ADDRESS_SUGGEST}
                    />
                  )}
                </>
              );
            }}
          </FormSpy>
        </FullWidthRowFormContainer>

        <Divider className={styles.fullWidthRow} style={{ marginTop: 0 }} />

        <CustomButton
          type="button"
          name={'Дополнительные контакты'}
          id="additional-contacts"
          onClick={toggleAdditionalFields}
          icon={showAdditionalFields ? <Icon name="down-arrow" className={cn(styles.iconDownArrow, styles.iconUpArrow)} /> : <Icon name="down-arrow" className={cn(styles.iconDownArrow)} />}
          className={cn(styles.toggleButton, styles.fullWidthRow)}
        />

        <FullWidthRowFormContainer>
          <p className={styles.additionalDesc} style={{ marginTop: 8 }}>Рекомендуем заполнить данные для оперативной связи по вопросам.</p>
          <p className={styles.additionalDesc}>При оставлении полей пустыми мы автоматически заполним их вашими контактами.</p>
        </FullWidthRowFormContainer>

        <div className={cn(styles.customCollapse, styles.fullWidthRow, { [styles.expanded]: showAdditionalFields })}>
          <div className={styles.collapseContent}>

            <FormField
              titleField={configTitleFieldsOfIndividualEntrepreneur[NamesOfFieldEnum.BuhName]}
              name={NamesOfFieldEnum.BuhName}
              tooltip="Укажите контактные данные лица, которое принимает решения по финансовым вопросам проекта"

              triggerAutofill={[NamesOfFieldEnum.BuhName, NamesOfFieldEnum.CrmName]}
            />

            <FormField
              titleField={configTitleFieldsOfIndividualEntrepreneur[NamesOfFieldEnum.BuhPhone]}
              name={NamesOfFieldEnum.BuhPhone}
              triggerAutofill={[NamesOfFieldEnum.BuhPhone, NamesOfFieldEnum.CrmPhone]}
            >
              <Field
                name={NamesOfFieldEnum.BuhPhone}
                subscription={{ submitFailed: true, error: true, submitError: true, value: true }}
                component={props => <PhoneField titleField={configTitleFieldsOfIndividualEntrepreneur[NamesOfFieldEnum.BuhPhone]}  {...props} />}
              />
            </FormField>

            <FormField
              titleField={configTitleFieldsOfIndividualEntrepreneur[NamesOfFieldEnum.TechName]}
              name={NamesOfFieldEnum.TechName}
              tooltip="Укажите контактные данные лица, которое принимает решения по техническим вопросам проекта"

              triggerAutofill={[NamesOfFieldEnum.TechName, NamesOfFieldEnum.CrmName]}
            />

            <FormField
              titleField={configTitleFieldsOfIndividualEntrepreneur[NamesOfFieldEnum.TechPhone]}
              name={NamesOfFieldEnum.TechPhone}
              triggerAutofill={[NamesOfFieldEnum.TechPhone, NamesOfFieldEnum.CrmPhone]}
            >
              <Field
                name={NamesOfFieldEnum.TechPhone}
                subscription={{ submitFailed: true, error: true, submitError: true, value: true }}
                component={props => <PhoneField titleField={configTitleFieldsOfIndividualEntrepreneur[NamesOfFieldEnum.TechPhone]}  {...props} />}
              />
            </FormField>
          </div>
        </div>
      </div>
    </V2Wizard.Page>
  );
};
